import Alert from '@/models/general/alert'
import { VuexResponse } from '@/helpers/vuex-action-builder'
import { ActionableRegistrationStatusReasons } from '@/constants/registrations/ActionableRegistrationStatusReasons'
import alertTypes from '@/constants/alerts/alertTypes'
import alertIds from '@/constants/alerts/alertIds'

const getDefaultState = () => {
  return {
    /**
     *  @property {Alert[]} alerts - Array of max length 2 (one for forms, one for registrations)
     */
    alerts: [],
    /**
     *  @property {boolean} initialized
     */
    initialized: false,
  }
}

const state = getDefaultState()

export default {
  namespaced: true,
  state,
  getters: {
    hasAlerts: (state) => state.alerts.length > 0,
    alerts: (state) => state.alerts,
    formAlert: (state) =>
      state.alerts.find((alert) => alert.type === alertTypes.ACTION),
    registrationAlert: (state) =>
      state.alerts.find((alert) => alert.type === alertTypes.ACTION),
    isInitialized: (state) => state.initialized,
  },
  mutations: {
    SET_ALERTS(state, alerts) {
      state.alerts = alerts?.filter(Boolean) || []
    },
    DISMISS_ALERT(state, alertId) {
      state.alerts = state.alerts.filter((alert) => alert.id !== alertId)
    },
    RESET(state) {
      Object.assign(state, getDefaultState())
    },
    SET_INITIALIZED(state, value = true) {
      state.initialized = value
    },
  },
  actions: {
    dismissAlert({ commit }, alertId) {
      commit('DISMISS_ALERT', alertId)
    },
    async initAlerts({ commit, dispatch, state }) {
      if (state.initialized) {
        return
      }
      commit('RESET')
      await dispatch('getAlerts')
      commit('SET_INITIALIZED')
    },
    async getAlerts({ commit, dispatch }) {
      const { data: registrationAlert } = await dispatch(
        'getRegistrationAlerts'
      )
      const { data: formAlert } = await dispatch('getFormAlerts')

      commit('SET_ALERTS', [registrationAlert, formAlert])
    },
    async getFormAlerts({ commit }) {
      return await new VuexResponse(commit)
        .request(() => this.$api.forms.getFormsStatus())
        .transform((data) => {
          const forms = data?.forms
          if (!forms || forms.length === 0) {
            return null
          }

          const isMultipleForms = forms?.length > 1

          return new Alert({
            id: alertIds.FORM,
            type: alertTypes.ACTION,
            title: isMultipleForms
              ? this.$i18n.t('alerts.multipleFormsAlertText')
              : this.$i18n.t('alerts.singleFormAlertText', {
                  title: forms[0].title,
                }),

            actions: [
              {
                text: this.$i18n.tc('alerts.goToFormText', forms.length),
                form: isMultipleForms ? null : forms[0], // If multiple then set null so action handler uses defined action
                action: function () {
                  if (this.$route.path !== '/my-files') {
                    this.$router.push({
                      path: '/my-files',
                      query: { tab: 'forms' },
                    })
                  }

                  this.$store.dispatch('alert/dismissAlert', alertIds.FORM)
                },
              },
            ],
          })
        })
        .go()
    },
    async getRegistrationAlerts({ commit }) {
      return await new VuexResponse(commit)
        .request(() => this.$api.registrations.getRegistrations())
        .transform((data) => {
          const registrationsAwaitingAction = data?.filter((registration) =>
            ActionableRegistrationStatusReasons.includes(
              registration.statusReason
            )
          )

          if (registrationsAwaitingAction?.length === 0) {
            return null
          }

          const regoId = registrationsAwaitingAction[0]?.id
          const path = regoId ? `/${regoId}` : ''

          return new Alert({
            id: alertIds.REGISTRATION,
            type: alertTypes.ACTION,
            title: this.$i18n.t('alerts.registrationAlertText'),
            actions: [
              {
                text: this.$i18n.t('alerts.goToRegistrationText'),
                action: function () {
                  if (this.$route.path !== '/registrations') {
                    this.$router.push(`/registrations${path}`)
                  }

                  this.$store.dispatch(
                    'alert/dismissAlert',
                    alertIds.REGISTRATION
                  )
                },
              },
            ],
          })
        })
        .go()
    },
  },
}
